// src/components/Admin/AdminDashboard.jsx
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAdminBookings,
  fetchAdminUsers,
  fetchAdminRooms,
  fetchAdminDogs,
  fetchAdminStats,
  fetchAdminProducts,
  fetchAdminOrders,
  updateBookingStatusAdmin,
  deleteRoomAdmin,
  deleteDogAdmin,
} from '../../redux/slices/Admin/adminSlice';
import AdminErrorBoundary from './AdminErrorBoundary';
import StatsTab from './tabs/StatsTab';
import BookingsTab from './tabs/BookingsTab';
import UsersTab from './tabs/UsersTab';
import RoomsTab from './tabs/RoomsTab';
import DogsTab from './tabs/DogsTab';
import ProductsTab from './tabs/ProductsTab';
import OrdersTab from './tabs/OrdersTab';
import AdminBlogPosts from './AdminBlogPosts';

const TABS = [
  { key: 'stats', label: 'Estadísticas' },
  { key: 'bookings', label: 'Reservas' },
  { key: 'users', label: 'Usuarios' },
  { key: 'rooms', label: 'Cuartos' },
  { key: 'dogs', label: 'Perros' },
  { key: 'products', label: 'Productos' },
  { key: 'orders', label: 'Pedidos' },
  { key: 'blog', label: 'Blog' },
];

const AdminDashboard = () => {
  const dispatch = useDispatch();
  const adminState = useSelector((state) => state.admin);
  const { bookings = [], users = [], rooms = [], dogs = [], products = [], orders = [] } = adminState || {};
  const [activeTab, setActiveTab] = useState('stats');

  useEffect(() => {
    dispatch(fetchAdminStats());
    dispatch(fetchAdminBookings());
    dispatch(fetchAdminUsers());
    dispatch(fetchAdminRooms());
    dispatch(fetchAdminDogs());
    dispatch(fetchAdminProducts());
    dispatch(fetchAdminOrders());
  }, [dispatch]);

  const handleStatusChange = (id, status) => dispatch(updateBookingStatusAdmin({ id, status }));
  const handleDeleteRoom = (id) => {
    if (window.confirm('Delete this room?')) dispatch(deleteRoomAdmin(id));
  };
  const handleDeleteDog = (id) => {
    if (window.confirm('Delete this dog record?')) dispatch(deleteDogAdmin(id));
  };

  return (
    <div className="bg-[#EFE6DD] min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {/* Header */}
        <div className="max-w-2xl mb-10">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9A0002]">
            Panel de administración
          </span>
          <h1 className="mt-2 font-['Fraunces'] text-4xl md:text-5xl font-semibold text-[#3A2A20]">
            Admin Dashboard
          </h1>
          <div className="mt-4 h-px w-24 border-t-2 border-dashed border-[#9A0002]/30" />
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 mb-8 bg-white/60 border border-[#3A2A20]/10 rounded-xl p-1 w-fit">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                activeTab === tab.key
                  ? 'bg-[#9A0002] text-[#EFE6DD]'
                  : 'text-[#3A2A20]/60 hover:bg-[#3A2A20]/5'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Cada tab aislado en su propio error boundary: si uno truena,
            los demás siguen funcionando y ves el error en pantalla en vez
            de una página en blanco. */}
        {activeTab === 'stats' && (
          <AdminErrorBoundary label="El tab de estadísticas">
            <StatsTab bookings={bookings} dogs={dogs} rooms={rooms} />
          </AdminErrorBoundary>
        )}

        {activeTab === 'bookings' && (
          <AdminErrorBoundary label="El tab de reservas">
            <BookingsTab bookings={bookings} onStatusChange={handleStatusChange} />
          </AdminErrorBoundary>
        )}

        {activeTab === 'users' && (
          <AdminErrorBoundary label="El tab de usuarios">
            <UsersTab users={users} bookings={bookings} />
          </AdminErrorBoundary>
        )}

        {activeTab === 'rooms' && (
          <AdminErrorBoundary label="El tab de cuartos">
            <RoomsTab rooms={rooms} onDeleteRoom={handleDeleteRoom} />
          </AdminErrorBoundary>
        )}

        {activeTab === 'dogs' && (
          <AdminErrorBoundary label="El tab de perros">
            <DogsTab dogs={dogs} onDeleteDog={handleDeleteDog} />
          </AdminErrorBoundary>
        )}

        {activeTab === 'products' && (
          <AdminErrorBoundary label="El tab de productos">
            <ProductsTab products={products} />
          </AdminErrorBoundary>
        )}

        {activeTab === 'orders' && (
          <AdminErrorBoundary label="El tab de pedidos">
            <OrdersTab orders={orders} />
          </AdminErrorBoundary>
        )}

        {activeTab === 'blog' && (
          <AdminErrorBoundary label="El tab de blog">
            <AdminBlogPosts />
          </AdminErrorBoundary>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;